import { useQuery } from '@tanstack/react-query'
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api } from '../api/client'
import type { UsageClient, UsageDashboardOut } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { PageHeader } from '../components/Layout'
import { Kpi } from '../components/Kpi'
import { Panel } from '../components/States'
import { AXIS, ChartTip, GRID } from '../components/Charts'
import { useParam } from '../lib/filters'
import { count, formatDateTime, hourLabel, humanise, shortDate } from '../lib/format'

const WINDOWS = ['7', '14', '30', '90'] as const
type Window = (typeof WINDOWS)[number]

const CLIENTS = ['all', 'app', 'web'] as const
type ClientFilter = (typeof CLIENTS)[number]

const CLIENT_LABEL: Record<UsageClient, string> = {
  app: 'Phone app',
  web: 'Web dashboard',
  other: 'Other (webhooks, scripts)',
  unknown: 'Before tracking',
}
const CLIENT_TONE: Record<UsageClient, string> = { app: 'blue', web: 'purple', other: 'grey', unknown: 'grey' }

function ClientChip({ client }: { client: UsageClient }) {
  return <span className={`chip ${CLIENT_TONE[client]}`}>{client === 'app' ? 'App' : client === 'web' ? 'Web' : CLIENT_LABEL[client]}</span>
}

export function UsagePage() {
  const { rootAdmin } = useAuth()
  const [days, setDays] = useParam<Window>('days', '7', WINDOWS)
  const [client, setClient] = useParam<ClientFilter>('client', 'all', CLIENTS)

  const query = useQuery({
    queryKey: ['usage', days, client],
    enabled: rootAdmin,
    queryFn: () =>
      api.get<UsageDashboardOut>('/usage/dashboard', {
        days: Number(days),
        client: client === 'all' ? null : client,
      }),
  })

  const data = query.data
  const daily = (data?.daily ?? []).map((point) => ({
    label: shortDate(point.date),
    requests: point.requests,
    app: point.app_requests,
    web: point.web_requests,
    // Webhooks, scripts, and anything recorded before clients were tracked.
    rest: point.requests - point.app_requests - point.web_requests,
    users: point.active_users,
  }))
  const totalRequests = (data?.by_client ?? []).reduce((sum, row) => sum + row.api_requests, 0)
  const hours = (data?.by_hour ?? []).map((point) => ({
    label: hourLabel(point.hour),
    count: point.count,
  }))

  if (!rootAdmin) {
    return (
      <>
        <PageHeader title="App usage" />
        <div className="content">
          <div className="alert warn">
            App-usage analytics is restricted to the primary <code>admin</code> account.
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title="App usage"
        subtitle={`Trailing ${days} days${client === 'all' ? '' : ` · ${CLIENT_LABEL[client]} only`}`}
      >
        <div className="seg" role="group" aria-label="Client">
          {CLIENTS.map((option) => (
            <button key={option} className={client === option ? 'on' : ''} onClick={() => setClient(option)}>
              {option === 'all' ? 'All' : option === 'app' ? 'Phone app' : 'Web'}
            </button>
          ))}
        </div>
        <div className="seg">
          {WINDOWS.map((option) => (
            <button key={option} className={days === option ? 'on' : ''} onClick={() => setDays(option)}>
              {option}d
            </button>
          ))}
        </div>
      </PageHeader>

      <div className="content stack">
        <Panel isLoading={query.isPending} error={query.error}>
          {data ? (
            <>
              <div className="grid cols-4">
                <Kpi label="Active users" value={count(data.kpi.active_users)} />
                <Kpi label="Sign-ins" value={count(data.kpi.logins)} />
                <Kpi label="API requests" value={count(data.kpi.api_requests)} />
                <Kpi label="Screen views" value={count(data.kpi.screen_views)} />
              </div>

              <div className="card">
                <div className="card-head">
                  <h2>Phone app vs web</h2>
                  <span className="hint">Told apart by each request's User-Agent · always both clients</span>
                </div>
                <div className="card-body flush">
                  <Panel isLoading={false} error={null} isEmpty={data.by_client.length === 0}>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Client</th>
                            <th className="num">Active users</th>
                            <th className="num">Sign-ins</th>
                            <th className="num">API requests</th>
                            <th style={{ width: '22%' }}>Share of requests</th>
                            <th className="num">Screen views</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.by_client.map((row) => {
                            const share = totalRequests ? (row.api_requests / totalRequests) * 100 : 0
                            return (
                              <tr key={row.client}>
                                <td>
                                  <ClientChip client={row.client} />{' '}
                                  <span className="cell-sub">{row.client === 'app' || row.client === 'web' ? CLIENT_LABEL[row.client] : ''}</span>
                                </td>
                                <td className="num">{count(row.active_users)}</td>
                                <td className="num">{count(row.logins)}</td>
                                <td className="num">{count(row.api_requests)}</td>
                                <td>
                                  <div className="row" style={{ alignItems: 'center', gap: 8 }}>
                                    <div className="bar" style={{ flex: 1 }}>
                                      <span style={{ width: `${share}%`, background: `var(--${CLIENT_TONE[row.client]})` }} />
                                    </div>
                                    <span className="cell-sub">{share.toFixed(0)}%</span>
                                  </div>
                                </td>
                                <td className="num">{count(row.screen_views)}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                    {data.by_client.some((row) => row.client === 'unknown') ? (
                      <p className="hint" style={{ padding: '10px 16px' }}>
                        “Before tracking” is activity recorded before the client was captured (26 Sept 2026); it
                        can’t be split after the fact.
                      </p>
                    ) : null}
                  </Panel>
                </div>
              </div>

              <div className="card">
                <div className="card-head">
                  <h2>Activity by day</h2>
                </div>
                <div className="card-body">
                  <Panel isLoading={false} error={null} isEmpty={daily.length === 0}>
                    <div className="chart">
                      <ResponsiveContainer>
                        <ComposedChart data={daily} margin={{ top: 6, right: 10, bottom: 0, left: 0 }}>
                          <defs>
                            <linearGradient id="usageFill" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="var(--blue)" stopOpacity={0.22} />
                              <stop offset="100%" stopColor="var(--blue)" stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid {...GRID} vertical={false} />
                          <XAxis dataKey="label" {...AXIS} minTickGap={24} />
                          <YAxis yAxisId="req" {...AXIS} width={52} />
                          <YAxis yAxisId="users" orientation="right" {...AXIS} width={36} />
                          <Tooltip content={<ChartTip />} cursor={{ stroke: 'var(--border-strong)' }} />
                          {client === 'all' ? (
                            <>
                              <Area yAxisId="req" type="monotone" stackId="c" dataKey="app" name="Phone app" stroke="var(--blue)" fill="var(--blue)" fillOpacity={0.25} />
                              <Area yAxisId="req" type="monotone" stackId="c" dataKey="web" name="Web" stroke="var(--purple)" fill="var(--purple)" fillOpacity={0.25} />
                              <Area yAxisId="req" type="monotone" stackId="c" dataKey="rest" name="Other / before tracking" stroke="var(--grey)" fill="var(--grey)" fillOpacity={0.15} />
                            </>
                          ) : (
                            <Area
                              yAxisId="req"
                              type="monotone"
                              dataKey="requests"
                              name="API requests"
                              stroke="var(--blue)"
                              strokeWidth={2}
                              fill="url(#usageFill)"
                            />
                          )}
                          <Line
                            yAxisId="users"
                            type="monotone"
                            dataKey="users"
                            name="Active users"
                            stroke="var(--brand)"
                            strokeWidth={2}
                            dot={false}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </Panel>
                </div>
              </div>

              <div className="card">
                <div className="card-head">
                  <h2>Per user</h2>
                </div>
                <div className="card-body flush">
                  <Panel isLoading={false} error={null} isEmpty={data.per_user.length === 0}>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Role</th>
                            <th className="num">Sign-ins</th>
                            <th>Uses</th>
                            <th className="num">API requests</th>
                            <th className="num">App</th>
                            <th className="num">Web</th>
                            <th className="num">Screen views</th>
                            <th className="num">Last active</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.per_user.map((row) => (
                            <tr key={row.username}>
                              <td className="strong">
                                {row.full_name}
                                <div className="cell-sub">{row.username}</div>
                              </td>
                              <td className="cell-sub">{humanise(row.role)}</td>
                              <td className="num">{count(row.logins)}</td>
                              <td>
                                <div className="row" style={{ gap: 4 }}>
                                  {row.last_app ? <ClientChip client="app" /> : null}
                                  {row.last_web ? <ClientChip client="web" /> : null}
                                  {!row.last_app && !row.last_web ? <span className="cell-sub">—</span> : null}
                                </div>
                              </td>
                              <td className="num">{count(row.api_requests)}</td>
                              <td className="num">{count(row.app_requests)}</td>
                              <td className="num">{count(row.web_requests)}</td>
                              <td className="num">{count(row.screen_views)}</td>
                              <td className="num cell-sub">{formatDateTime(row.last_active)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Panel>
                </div>
              </div>

              <div className="grid cols-2">
                <div className="card">
                  <div className="card-head">
                    <h2>Busiest hours</h2>
                  </div>
                  <div className="card-body">
                    <Panel isLoading={false} error={null} isEmpty={hours.length === 0}>
                      <div className="chart">
                        <ResponsiveContainer>
                          <BarChart data={hours} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                            <CartesianGrid {...GRID} vertical={false} />
                            <XAxis dataKey="label" {...AXIS} interval={2} />
                            <YAxis {...AXIS} width={48} />
                            <Tooltip content={<ChartTip />} cursor={{ fill: 'var(--page)' }} />
                            <Bar dataKey="count" name="Requests" fill="var(--teal)" radius={[3, 3, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </Panel>
                  </div>
                </div>

                <div className="card">
                  <div className="card-head">
                    <h2>Recent sign-ins</h2>
                  </div>
                  <div className="card-body flush">
                    <Panel isLoading={false} error={null} isEmpty={data.recent_logins.length === 0}>
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th>User</th>
                              <th>From</th>
                              <th className="num">When</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.recent_logins.map((row, index) => (
                              <tr key={`${row.username}-${row.at}-${index}`}>
                                <td>
                                  {row.full_name}
                                  <div className="cell-sub">{row.username}</div>
                                </td>
                                <td>
                                  <ClientChip client={row.client} />
                                </td>
                                <td className="num cell-sub">{formatDateTime(row.at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Panel>
                  </div>
                </div>
              </div>

              <div className="grid cols-2">
                <div className="card">
                  <div className="card-head">
                    <h2>Top endpoints</h2>
                  </div>
                  <div className="card-body flush">
                    <Panel isLoading={false} error={null} isEmpty={data.top_endpoints.length === 0}>
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th>Endpoint</th>
                              <th className="num">Calls</th>
                              <th className="num">Avg</th>
                              <th className="num">Errors</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.top_endpoints.map((row) => (
                              <tr key={`${row.method} ${row.path}`}>
                                <td>
                                  <span className="chip grey">{row.method}</span>{' '}
                                  <span className="truncate" style={{ display: 'inline-block' }}>
                                    {row.path}
                                  </span>
                                </td>
                                <td className="num">{count(row.count)}</td>
                                <td className="num">{count(row.avg_duration_ms)} ms</td>
                                <td className="num">
                                  {row.error_count ? (
                                    <span className="trend down">{count(row.error_count)}</span>
                                  ) : (
                                    <span className="cell-sub">0</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Panel>
                  </div>
                </div>

                <div className="card">
                  <div className="card-head">
                    <h2>Top screens</h2>
                  </div>
                  <div className="card-body flush">
                    <Panel isLoading={false} error={null} isEmpty={data.top_screens.length === 0}>
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th>Screen</th>
                              <th>From</th>
                              <th className="num">Views</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.top_screens.map((row) => (
                              <tr key={`${row.client} ${row.path}`}>
                                <td>{row.path}</td>
                                <td>
                                  <ClientChip client={row.client} />
                                </td>
                                <td className="num">{count(row.views)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Panel>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </Panel>
      </div>
    </>
  )
}
